import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { OptionWindow } from 'src/app/enum/option-window-enums';
import { ProfileUserEnums } from 'src/app/enum/profile-user-enums';
import { WindowEnums } from 'src/app/enum/window-enums';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { OptionWindowModel } from 'src/app/models/authentication/option-window.model';
import { UserModel } from 'src/app/models/authentication/user.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { InvoicingModelDetailTableModel } from 'src/app/models/tables/management/invoicing-model-detail-table.model';
import { InvoicingModelTableModel } from 'src/app/models/tables/management/invoicing-model-table.model';
import { OptionWindowService } from 'src/app/services/authentication/option-window.service';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { InvoicingModelDetailService } from 'src/app/services/invoicing/invoicing-model-detail.service';
import { InvoicingModelService } from 'src/app/services/invoicing/invoicing-model.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { WindowService } from './../../../../services/authentication/window.service';
import { ConceptInvoicingTypeEnums } from 'src/app/enum/concept-invoicing-enums';

@Component({
  selector: 'app-invoicing-model',
  templateUrl: './invoicing-model.component.html',
  styleUrls: ['./invoicing-model.component.scss'],
  standalone: false
})
export class InvoicingModelComponent implements OnInit, OnDestroy {

  activeTabIndex = 0;
  ascendant: boolean = true;
  columnSort: string | null = null;
  conceptInvoicingType: number = 1;
  detailData: PagedResponseModel<InvoicingModelDetailTableModel> = new PagedResponseModel<InvoicingModelDetailTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  filters: ColumnResponseModel[] = [];
  generalData: PagedResponseModel<InvoicingModelTableModel> = new PagedResponseModel<InvoicingModelTableModel>();
  generalHeader: ColumnResponseModel[] = [];
  isOpened: string | undefined = undefined;
  limit: number = 10;
  nameWindow: string = 'Modelos de Facturación';
  page: number = 1;
  permissionUpdatable: boolean = false;
  permissionsUser: Array<OptionWindowModel> = new Array<OptionWindowModel>();
  projectName = ProjectName.projectName;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    },
  ];
  url: string = "invoicing-model";
  userAuthenticated: UserModel = new UserModel();

  constructor(private headerService: HeaderService,
    private invoicingModelService: InvoicingModelService,
    private invoicingModelDetailService: InvoicingModelDetailService,
    private optionWindowService: OptionWindowService,
    private router: Router,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private userAuthenticatedService: UserAuthenticatedService,
    private windowService: WindowService) {
  }

  async ngOnInit() {
    this.getPermission();
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    const filters = localStorage.getItem(this.url);
    if (filters) this.filters = JSON.parse(filters);
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getInvoicingModelsWithPagination(),
    ]);
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  async ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  createNewRegister() {
    if (this.permissionUpdatable) {
      this.router.navigate([`/${this.url}/register`]);
    } else {
      this.toastHandlerService.showToast(
        'error',
        `El usuario no tiene permisos para crear ${this.nameWindow}. Comuníquese con el administrador del sistema.`
      );
    }
  }

  async exportDetailConsultation() {
    if (this.userAuthenticated.profileUser.code == ProfileUserEnums.administrador || this.permissionsUser.find(x => x.code == OptionWindow.Exportar)) {
      try {
        const response = await firstValueFrom(this.invoicingModelDetailService.exportDetailConsultation(this.filters, this.columnSort, this.ascendant));
        const blob = response;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Modelos de Facturación Detalles.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error al exportar el detalle de los Modelos de Facturación.', error);
      }
    } else {
      this.toastHandlerService.showToast(
        'warning',
        'El usuario no tiene permisos para exportar el detalle de Modelos de Facturación, comuniquese con el administrador del sistema.'
      );
    }
  }

  async exportGeneralConsultation() {
    if (this.userAuthenticated.profileUser.code == ProfileUserEnums.administrador || this.permissionsUser.find(x => x.code == OptionWindow.Exportar)) {
      const isCompra = this.conceptInvoicingType === ConceptInvoicingTypeEnums.compra;
      const modelName = isCompra ? 'Compra' : 'Facturación';
      try {
        let response;
        if (isCompra) {
          response = await firstValueFrom(this.invoicingModelService.exportGeneralConsultationPurchaseModel(this.filters, this.columnSort, this.ascendant));
        } else {
          response = await firstValueFrom(this.invoicingModelService.exportGeneralConsultation(this.filters, this.columnSort, this.ascendant));
        }
        const blob = response;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Modelos de ${modelName}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error(`Error al exportar los Modelos de ${modelName}.`, error);
      }
    } else {
      const modelName = this.conceptInvoicingType === ConceptInvoicingTypeEnums.compra ? 'Compra' : 'Facturación';
      this.toastHandlerService.showToast(
        'warning',
        `El usuario no tiene permisos para exportar los Modelos de ${modelName}, comuniquese con el administrador del sistema.`
      );
    }
  }

  async getColumnsForDetailsConsultation() {
    try {
      if (this.detailHeader.length === 0) {
        if (this.conceptInvoicingType === ConceptInvoicingTypeEnums.compra) {
          this.detailHeader = await firstValueFrom(this.invoicingModelDetailService.getColumnsForPurchaseDetailConsultation());
        } else {
          this.detailHeader = await firstValueFrom(this.invoicingModelDetailService.getColumnsForDetailConsultation());
        }
      }
    } catch (error) {
      console.error('Error al obtener las columnas para consulta detalle.', error);
    }
  }

  async getColumnsForGeneralConsultation() {
    const message = this.conceptInvoicingType === ConceptInvoicingTypeEnums.venta ? 'Facturación' : 'Compra';
    try {
      if (this.generalHeader.length === 0) {
        if (this.conceptInvoicingType === ConceptInvoicingTypeEnums.compra) {
          this.generalHeader = await firstValueFrom(this.invoicingModelService.getColumnsForGeneralConsultationPurchaModel());
        } else {
          this.generalHeader = await firstValueFrom(this.invoicingModelService.getColumnsForGeneralConsultation());
        }
      }
    } catch (error) {
      console.error(`Error al obtener las columnas para consulta general de ${message}.`, error);
    }
  }

  async getInvoicingModelDetailsWithPagination() {
    try {
      if (this.conceptInvoicingType === ConceptInvoicingTypeEnums.compra) {
        this.detailData = await firstValueFrom(
          this.invoicingModelDetailService.getAllPurchaseModelDetailsWithPagination(this.page, this.limit, this.columnSort, this.ascendant, this.filters)
        );
      } else {
        this.detailData = await firstValueFrom(
          this.invoicingModelDetailService.getAllInvoicingModelDetailsWithPagination(this.page, this.limit, this.columnSort, this.ascendant, this.filters)
        );
      }
    } catch (error) {
      console.error('Error al buscar los detalles.', error);
    }
  }

  async getInvoicingModelsWithPagination() {
    const message = this.conceptInvoicingType === ConceptInvoicingTypeEnums.venta ? 'Facturación' : 'Compra';
    try {
      if (this.conceptInvoicingType === ConceptInvoicingTypeEnums.compra) {
        this.generalData = await firstValueFrom(this.invoicingModelService.getAllPurchaseModelsWithPagination(this.page, this.limit, this.columnSort, this.ascendant, this.filters));
      } else {
        this.generalData = await firstValueFrom(this.invoicingModelService.getAllInvoicingModelsWithPagination(this.page, this.limit, this.columnSort, this.ascendant, this.filters));
      }
    } catch (error) {
      console.error(`Error al buscar los modelos de ${message}.`, error);
    }
  }

  async getPermission() {
    const url = this.router.url as string;
    if (url && url !== '/') {
      this.url = url.startsWith('/') ? url.substring(1) : url;
    }

    this.conceptInvoicingType = this.url === 'purchase-model' ? ConceptInvoicingTypeEnums.compra : ConceptInvoicingTypeEnums.venta;
    this.nameWindow = this.conceptInvoicingType === ConceptInvoicingTypeEnums.compra ? 'Modelos de Compra' : 'Modelos de Facturación';

    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      console.error('Error al obtener los permisos.', error);
    }
    this.getPermissionsForUserByWindow();
  }

  async getPermissionsForUserByWindow() {
    this.userAuthenticated = await firstValueFrom(this.userAuthenticatedService.userObservable.pipe(filter(user => user && user.userId !== 0)));
    this.permissionsUser = await firstValueFrom(this.optionWindowService.getPermissionsForUserByWindow(WindowEnums.InvoicingModel));
  }

  public async handleTabChange(tabIndex: number) {
    this.generalHeader.length = this.generalHeader.length === 1 ? 0 : this.generalHeader.length;
    this.detailHeader.length = this.detailHeader.length === 1 ? 0 : this.detailHeader.length;

    if (this.activeTabIndex === tabIndex) return;

    this.page = 1;
    this.limit = 10;
    this.activeTabIndex = tabIndex;

    switch (this.activeTabIndex) {
      case 0:
        await Promise.all([
          this.getColumnsForGeneralConsultation(),
          this.getInvoicingModelsWithPagination()
        ]);
        this.generalData = {
          ...this.generalData,
          currentPage: 1,
          pageSize: 10
        };
        break;
      case 1:
        await Promise.all([
          this.getColumnsForDetailsConsultation(),
          this.getInvoicingModelDetailsWithPagination()
        ]);
        this.detailData = {
          ...this.detailData,
          currentPage: 1,
          pageSize: 10
        };
        break;
    }
  }

  onAction(event: { action: string, item?: any, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) { this.router.navigate([`/${this.url}/register/${event.item.id}`]); }
  }

  async onExport() {
    switch (this.activeTabIndex) {
      case 0:
        await this.exportGeneralConsultation();
        break;
      case 1:
        await this.exportDetailConsultation();
        break;
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    switch (this.activeTabIndex) {
      case 0:
        await this.getInvoicingModelsWithPagination()
        break;
      case 1:
        await this.getInvoicingModelDetailsWithPagination()
        break;
    }
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

}
